import { BehaviorSubject, filter, map, Observable, of, Subscription } from 'rxjs';

import {
    ChatHistoryPageQuery,
    ChatManagerState,
    ChatMessageSearchItem,
    ChatOutgoingMessage,
    ChatPageHasMore,
    ChatSessionItem,
    ChatSessionsPageState,
} from 'src/app/cyn/chat-module/types/chat-session.types';
import { ChatAgentProvider } from '../providers/chat-agent.provider';
import { ChatAgentSessionProvider } from '../providers/chat-agent-session.provider';
import { AGENT_ERROR_CODE, WsErrorEvent, WsSessionInfo } from '../ws/agent-ws.types';
import { AgentChatSession } from '../sessions/agent-chat.session';
import { AgentChatTemplate } from '../templates/agent-chat.template';
import { ChatLinkBlockComponent } from '../../templates/blocks/chat-link-block/chat-link-block.component';
import { ChatRegistryLinkBlockComponent } from '../../templates/blocks/chat-registry-link-block/chat-registry-link-block.component';
import { ChatExcelFileBlockComponent } from '../../templates/blocks/chat-excel-file-block/chat-excel-file-block.component';
import { AgentTool } from '../tools/agent-tool.types';
import { ChatFormDraftBlockComponent } from '../../templates/blocks/chat-form-draft-block/chat-form-draft-block.component';
import { listRegistryLabels } from '../registries/registry-catalog';
import { BaseChatManager } from 'src/app/cyn/chat-module/core/managers/base-chat.manager';
import { AGENT_ERROR_MESSAGES, AgentErrorMessage, resolveAgentErrorMessage } from '../errors/agent-error-messages';
import { formatSessionUpdatedAtLabel } from '../utils/format-session-updated-at';
import {
    buildNewerSessionsPage,
    buildOlderSessionsPage,
    cloneSessionsPage,
    FIRST_SESSIONS_PAGE,
    normalizeSessionsPage,
    sessionsPageBefore,
} from '../utils/chat-pagination';
import { AiChatSessionRenameService } from '../../services/ai-chat-session-rename.service';
import { AiChatAlertService } from '../../services/ai-chat-alert.service';
import { AiChatAttachmentsService } from 'src/app/services/ai-chat-attachments.service';
import { AiChatSessionStateStore } from 'src/app/state/ai-chat-session-state.store';
import { ChatAttachmentMessageComponent } from 'src/app/cyn/chat-module/components/session-view/message-types/chat-attachment-message/chat-attachment-message.component';

const DEFAULT_SESSION_TITLE = 'Диалог';
const NEW_SESSION_TITLE = 'Новый диалог';

/**
 * Менеджер чата с AI-агентом для текущего проекта.
 * Использует WebSocket-протокол Chat V2.
 * Держит настройку режима чата и передаёт UI подходящую agent-сессию.
 */
export class AgentManager extends BaseChatManager {
    /** @inheritdoc */
    public override readonly label = 'Ассистент';

    /** @inheritdoc */
    public override readonly icon?: string;

    /** @inheritdoc */
    public override readonly template: AgentChatTemplate;
    /** @inheritdoc */
    public override readonly sessions$: Observable<ChatSessionItem[]>;

    /** @inheritdoc */
    public override readonly activeSession$: Observable<AgentChatSession | null>;

    /** @inheritdoc */
    public override readonly state$: Observable<ChatManagerState>;

    /** @inheritdoc */
    public override readonly totalUnreadCount$: Observable<number>;

    /** @inheritdoc */
    public override readonly sessionsHasMore$: Observable<ChatPageHasMore>;

    /** @inheritdoc */
    public override readonly sessionsIsLoadingMore$: Observable<ChatPageHasMore>;

    private readonly agentProvider: ChatAgentProvider;
    private readonly renameService: AiChatSessionRenameService;
    private readonly attachmentsService: AiChatAttachmentsService;
    private readonly alertService: AiChatAlertService;
    private readonly stateStore: AiChatSessionStateStore;
    private readonly sessionsSubject = new BehaviorSubject<ChatSessionItem[]>([]);
    private readonly activeSessionSubject = new BehaviorSubject<AgentChatSession | null>(null);
    private readonly stateSubject = new BehaviorSubject<ChatManagerState>({ status: 'loading' });
    private readonly sessionsHasMoreSubject = new BehaviorSubject<ChatPageHasMore>({ before: false, after: false });
    private readonly sessionsIsLoadingMoreSubject = new BehaviorSubject<ChatPageHasMore>({ before: false, after: false });
    private readonly subscriptions = new Subscription();
    private currentSession: AgentChatSession | null = null;
    private sessionsPage: ChatSessionsPageState = cloneSessionsPage(FIRST_SESSIONS_PAGE);
    private sessionsRequestInFlight = false;
    private pendingSessionsFetch: {
        page: ChatSessionsPageState;
        options: {
            showLoading: boolean;
            fallbackToFirst?: boolean;
            ensureSessionId?: string;
        };
    } | null = null;

    public constructor(
        wsUrl: string,
        tabId: string,
        tools: AgentTool[] = [],
        renameService: AiChatSessionRenameService,
        attachmentsService: AiChatAttachmentsService,
        alertService: AiChatAlertService,
        stateStore: AiChatSessionStateStore,
    ) {
        super();

        this.renameService = renameService;
        this.attachmentsService = attachmentsService;
        this.alertService = alertService;
        this.stateStore = stateStore;

        this.template = new AgentChatTemplate({
            title: 'AI Ассистент',
            chatMessageViewConfig: {
                showAvatar: false,
                showDateTime: false,
            },
            chatManagerViewConfig: {
                showInputWithoutSession: true,
                chatSession: {
                    type: 'agent',
                    menuItems: (session) => [
                        {
                            text: 'Редактировать',
                            onClick: () => this.renameSession(session),
                        },
                        {
                            text: 'Удалить',
                            isDanger: true,
                            onClick: () => this.deleteSession(session.id),
                        },
                    ],
                },
                chatInput: {
                    allowFiles: true,
                    allowVoice: true,
                },
                emptySessionListInfo: {
                    title: 'С возвращением! Чем я могу помочь?',
                    text: 'Опишите задачу, задайте вопрос или запросите необходимую информацию.',
                    button: null,
                    secondButton: null,
                    caption: '',
                }
            }
        });

        this.template.registerMessage('file', ChatAttachmentMessageComponent);
        this.template.registerBlock('link', ChatLinkBlockComponent, {
            description:
                'Ссылка для перехода по внутреннему маршруту приложения. '
                + 'Перед сборкой блока ОБЯЗАТЕЛЬНО вызови тулзу get_app_pages и используй только path из её ответа как route (как есть, включая /core и #hash). '
                + 'Плейсхолдер {ID} замени на числовой id. Не выдумывай маршруты. '
                + 'Никогда не пиши ссылку в самом сообщении, если можно показать этот блок. '
                + 'Для фильтров реестра заявок/счетов используй registry_link.',
            schema: { route: 'string', label: 'string' },
        });
        this.template.registerBlock('registry_link', ChatRegistryLinkBlockComponent, {
            description:
                'Ссылка на отфильтрованный реестр приложения. '
                + `Поддерживаемые registry: ${listRegistryLabels().join(', ')}. `
                + 'Перед сборкой блока ОБЯЗАТЕЛЬНО вызови тулзу get_registry_filters(registry) и используй только ключи/типы из её ответа. '
                + 'filters - объект ключ-значение query-параметров. '
                + 'Не пиши URL реестра в тексте сообщения - показывай этот блок. '
                + 'Для обычных внутренних ссылок без фильтров реестра используй блок link.',
            schema: {
                registry: 'string - id реестра (например orders)',
                filters: 'object - map of filterKey to value',
            },
        });
        this.template.registerBlock('form_draft', ChatFormDraftBlockComponent, {
            description: 'Карточка предзаполненной формы с отметкой посещения для МНОЖЕСТВЕННОГО запроса. Сначала вызови prepare_forms. '
                + 'Используй только если инструмент вернул mode=multiple. Для mode=single карточки не выводи. '
                + 'При нескольких объектах покажи по одной карточке на каждый draftId из ответа. '
                + 'Открытие формы не означает создание объекта: пользователь сам нажимает «Сохранить».',
            schema: { draftId: 'string — ID из prepare_forms' },
        });
        this.template.registerBlock('excel_file', ChatExcelFileBlockComponent, {
            description:
                'Карточка со ссылкой на скачивание Excel-файла. '
                + 'ОБЯЗАТЕЛЬНО сначала вызови тулзу create_excel_file и в поле fileId подставь fileId из её ответа. '
                + 'Никогда не выдумывай fileId и не переноси в блок данные таблицы. '
                + 'Показывай этот блок вместо ссылки на скачивание и вместо перечисления данных в тексте. '
                + 'Один успешный вызов create_excel_file — один блок excel_file.',
            schema: {
                fileId: 'string - id из ответа create_excel_file',
            },
        });

        this.agentProvider = new ChatAgentProvider(wsUrl, tabId, this.template.getBlockDeclarations(), tools);

        this.sessions$ = this.sessionsSubject.asObservable();
        this.activeSession$ = this.activeSessionSubject.asObservable();
        this.state$ = this.stateSubject.asObservable();
        this.sessionsHasMore$ = this.sessionsHasMoreSubject.asObservable();
        this.sessionsIsLoadingMore$ = this.sessionsIsLoadingMoreSubject.asObservable();
        this.totalUnreadCount$ = this.sessions$.pipe(
            map(items => items.reduce((acc, item) => acc + item.unreadCount, 0)),
        );

        this.subscriptions.add(
            this.agentProvider.sessionTitle$.subscribe(event => {
                this.updateSessionTitle(event.sessionId, event.title);
            }),
        );

        this.subscriptions.add(
            this.agentProvider.sessionTakenOver$.subscribe(() => this.handleSessionTakenOver()),
        );
    }

    public override searchSessions(_query: string): Observable<ChatSessionItem[]> { return of([]); }

    public override searchMessages(_query: string): Observable<ChatMessageSearchItem[]> { return of([]); }
    public override openSessionAtMessage(item: ChatMessageSearchItem): void { this.openSession(item.sessionId); }

    /** @inheritdoc */
    public override init(): void {
        this.sessionsPage = normalizeSessionsPage(this.stateStore.getState()?.ui?.sessionsPage);
        this.fetchSessions(this.sessionsPage, { showLoading: true, fallbackToFirst: true });
    }

    /** @inheritdoc */
    public override loadMoreSessions(direction: 'before' | 'after'): void {
        if (this.sessionsRequestInFlight) {
            return;
        }

        const hasMore = this.sessionsHasMoreSubject.getValue();

        if (direction === 'before') {
            if (!hasMore.before) {
                return;
            }

            const sessions = this.sessionsSubject.getValue();
            const lastSessionId = sessions[sessions.length - 1]?.id;
            if (!lastSessionId) {
                return;
            }

            this.fetchSessions(buildOlderSessionsPage(this.sessionsPage, lastSessionId), { showLoading: false });
            return;
        }

        if (!hasMore.after) {
            return;
        }

        const previousPage = buildNewerSessionsPage(this.sessionsPage);
        if (!previousPage) {
            return;
        }

        this.fetchSessions(previousPage, { showLoading: false });
    }

    /**
     * Создать новую сессию агента.
      * Использует общее WS-соединение агента.
     */
    public override createSession(initialMessage?: ChatOutgoingMessage): void {
        this.closeCurrentSession();
        this.stateSubject.next({ status: 'loading' });
        this.subscriptions.add(
            this.agentProvider.createSessionProvider().subscribe({
                next: provider => {
                    this.sessionsPage = cloneSessionsPage(FIRST_SESSIONS_PAGE);
                    this.persistSessionsPage();
                    this.activateSession(provider, initialMessage);
                    this.fetchSessions(this.sessionsPage, {
                        showLoading: false,
                        ensureSessionId: provider.id,
                    });
                },
                error: (error) => this.stateSubject.next(this.toErrorState(error, {
                    title: 'Не удалось создать новый диалог. Попробуйте ещё раз.',
                    text: 'Попробуйте обновить страницу или повторите позже.',
                })),
            }),
        );
    }

    /** @inheritdoc */
    public override openSession(sessionId: string, historyQuery?: ChatHistoryPageQuery): void {
        console.log(`Opening session with ID: ${sessionId}`);
        if (this.currentSession?.id === sessionId) {
            this.activeSessionSubject.next(this.currentSession);
            this.stateSubject.next({ status: 'ready' });
            return;
        }

        this.closeCurrentSession();
        this.stateSubject.next({ status: 'loading' });
        this.subscriptions.add(
            this.agentProvider.getSessionProvider(sessionId).subscribe({
                next: provider => this.activateSession(provider, undefined, historyQuery),
                error: (error) => this.stateSubject.next(this.toErrorState(error, {
                    title: 'Не удалось открыть диалог. Вернитесь в список диалогов и попробуйте ещё раз.',
                    text: 'Попробуйте обновить страницу или повторите позже.',
                })),
            }),
        );
    }

    /** @inheritdoc */
    public override closeSession(): void {
        this.closeCurrentSession();
    }

    /** @inheritdoc */
    public override deleteSession(sessionId: string): void {
        if (this.currentSession?.id === sessionId) {
            this.currentSession.destroy();
            this.currentSession = null;
            this.activeSessionSubject.next(null);
            this.stateSubject.next({ status: 'ready' });
        }

        this.subscriptions.add(
            this.agentProvider.deleteSession(sessionId).subscribe({
                error: (error) => this.stateSubject.next(this.toErrorState(error, {
                    title: 'Не удалось удалить диалог.',
                    text: 'Попробуйте обновить страницу или повторите позже.',
                })),
            }),
        );

        this.sessionsSubject.next(this.sessionsSubject.getValue().filter(session => session.id !== sessionId));
    }

    /** Открыть модалку и отправить новое название диалога на сервер. */
    public renameSession(session: ChatSessionItem): void {
        this.subscriptions.add(
            this.renameService.open(session.title).pipe(
                filter((title): title is string => !!title),
            ).subscribe({
                next: title => {
                    this.updateSessionTitle(session.id, title);
                    this.subscriptions.add(
                        this.agentProvider.setSessionTitle(session.id, title).subscribe({
                            error: (error) => this.stateSubject.next(this.toErrorState(error, {
                                title: 'Не удалось изменить название диалога.',
                                text: 'Попробуйте обновить страницу или повторите позже.',
                            })),
                        }),
                    );
                },
            }),
        );
    }

    /** @inheritdoc */
    public override destroy(): void {
        this.closeCurrentSession();
        this.subscriptions.unsubscribe();
        this.agentProvider.destroy();
        this.sessionsSubject.complete();
        this.activeSessionSubject.complete();
        this.stateSubject.complete();
        this.sessionsHasMoreSubject.complete();
        this.sessionsIsLoadingMoreSubject.complete();
    }

    private mapSessionInfo(info: WsSessionInfo): ChatSessionItem {
        return {
            id: info.sessionId,
            title: this.resolveSessionTitle(info.title, DEFAULT_SESSION_TITLE),
            updatedAtLabel: formatSessionUpdatedAtLabel(new Date(info.updatedAt)),
            unreadCount: 0,
        };
    }

    private loadSessions(): void {
        this.fetchSessions(this.sessionsPage, { showLoading: true, fallbackToFirst: true });
    }

    private fetchSessions(
        page: ChatSessionsPageState,
        options: {
            showLoading: boolean;
            fallbackToFirst?: boolean;
            ensureSessionId?: string;
        },
    ): void {
        if (this.sessionsRequestInFlight) {
            this.pendingSessionsFetch = { page, options };
            return;
        }

        this.sessionsRequestInFlight = true;
        const before = sessionsPageBefore(page);
        const goingOlder = page.index > this.sessionsPage.index;
        const goingNewer = page.index < this.sessionsPage.index;

        if (options.showLoading) {
            this.stateSubject.next({ status: 'loading' });
        }

        this.sessionsIsLoadingMoreSubject.next({
            before: goingOlder,
            after: goingNewer,
        });

        this.subscriptions.add(
            this.agentProvider.getSessions(before).subscribe({
                next: event => {
                    this.sessionsRequestInFlight = false;
                    this.sessionsIsLoadingMoreSubject.next({ before: false, after: false });

                    if (!event.sessions.length && before && options.fallbackToFirst !== false) {
                        this.pendingSessionsFetch = this.pendingSessionsFetch ?? {
                            page: cloneSessionsPage(FIRST_SESSIONS_PAGE),
                            options: {
                                showLoading: options.showLoading,
                                fallbackToFirst: false,
                                ensureSessionId: options.ensureSessionId,
                            },
                        };
                        this.flushPendingSessionsFetch();
                        return;
                    }

                    this.sessionsPage = cloneSessionsPage(page);
                    const items = event.sessions.map(session => this.mapSessionInfo(session));
                    this.sessionsSubject.next(this.ensureSessionItem(items, options.ensureSessionId));
                    this.sessionsHasMoreSubject.next({
                        before: Boolean(event.hasMore),
                        after: this.sessionsPage.index > 0,
                    });
                    this.persistSessionsPage();

                    if (options.showLoading || this.stateSubject.getValue().status === 'loading') {
                        if (!this.currentSession) {
                            this.stateSubject.next({ status: 'ready' });
                        }
                    }

                    this.flushPendingSessionsFetch();
                },
                error: (error) => {
                    this.sessionsRequestInFlight = false;
                    this.sessionsIsLoadingMoreSubject.next({ before: false, after: false });

                    if (this.currentSession || !options.showLoading) {
                        this.alertService.show(
                            'Не удалось загрузить список диалогов. Попробуйте обновить страницу или повторите позже.',
                        );
                        this.flushPendingSessionsFetch();
                        return;
                    }

                    this.stateSubject.next(this.toErrorState(error, {
                        title: 'Не удалось загрузить список диалогов.',
                        text: 'Попробуйте обновить страницу или повторите позже.',
                    }));
                    this.flushPendingSessionsFetch();
                },
            }),
        );
    }

    private flushPendingSessionsFetch(): void {
        const pending = this.pendingSessionsFetch;
        this.pendingSessionsFetch = null;

        if (pending) {
            this.fetchSessions(pending.page, pending.options);
        }
    }

    private persistSessionsPage(): void {
        this.stateStore.patchState({
            ui: {
                sessionsPage: cloneSessionsPage(this.sessionsPage),
            },
        });
    }

    private ensureSessionItem(items: ChatSessionItem[], sessionId?: string): ChatSessionItem[] {
        if (!sessionId || items.some(item => item.id === sessionId)) {
            return items;
        }

        return [this.createSessionItem(sessionId, NEW_SESSION_TITLE), ...items];
    }

    private createSessionItem(sessionId: string, title: string): ChatSessionItem {
        return {
            id: sessionId,
            title,
            updatedAtLabel: formatSessionUpdatedAtLabel(new Date()),
            unreadCount: 0,
        };
    }

    private activateSession(
        provider: ChatAgentSessionProvider,
        initialMessage?: ChatOutgoingMessage,
        historyQuery?: ChatHistoryPageQuery,
    ): void {
        const title = this.sessionsSubject.getValue().find(s => s.id === provider.id)?.title ?? DEFAULT_SESSION_TITLE;
        const session = new AgentChatSession(title, provider, this.attachmentsService, this.alertService, {
            initialHistoryQuery: historyQuery,
            stateStore: this.stateStore,
        });

        this.currentSession = session;
        this.sessionsSubject.next(
            this.sessionsSubject.getValue().map(item => item.id === provider.id ? { ...item, unreadCount: 0 } : item),
        );
        this.activeSessionSubject.next(session);
        session.activate();

        if (initialMessage) {
            session.send(initialMessage);
        }

        this.stateSubject.next({ status: 'ready' });
    }

    private updateSessionTitle(sessionId: string, title: string): void {
        const nextTitle = this.resolveSessionTitle(title, '');

        if (!nextTitle) {
            return;
        }

        const sessions = this.sessionsSubject.getValue();
        const index = sessions.findIndex(session => session.id === sessionId);

        if (index < 0 || sessions[index].title === nextTitle) {
            return;
        }

        const updated = [...sessions];
        updated[index] = { ...updated[index], title: nextTitle }; // Смущает полная перезапись объекта, возможны проблемы с гонками
        this.sessionsSubject.next(updated);
    }

    private resolveSessionTitle(title: string | undefined | null, fallback: string): string {
        const trimmed = typeof title === 'string' ? title.trim() : '';

        return trimmed || fallback;
    }

    private handleSessionTakenOver(): void {
        const message = AGENT_ERROR_MESSAGES[AGENT_ERROR_CODE.sessionTakenOver];

        this.closeCurrentSession();
        this.loadSessions();
        this.alertService.show(message.text, message.title);
    }

    private closeCurrentSession(): void {
        if (this.currentSession) {
            this.currentSession.destroy();
            this.currentSession = null;
            this.subscriptions.add(
                this.agentProvider.closeSessionProvider().subscribe({
                    error: (error) => this.stateSubject.next(this.toErrorState(error, {
                        title: 'Не удалось корректно закрыть активный диалог.',
                        text: 'Попробуйте обновить страницу или повторите позже.',
                    })),
                }),
            );
        }

        this.activeSessionSubject.next(null);

        this.stateSubject.next({ status: 'ready' });
    }

    private toErrorState(error: unknown, fallback: AgentErrorMessage): ChatManagerState {
        const message = resolveAgentErrorMessage(this.getErrorCode(error), fallback);

        return {
            status: 'error',
            errorMessage: message.title,
            errorText: message.text,
            hideHeaderBack: message.hideBackButton === true,
        };
    }

    private getErrorCode(error: unknown): string | undefined {
        if (!error || typeof error !== 'object') {
            return undefined;
        }

        const event = error as Partial<WsErrorEvent>;

        return event.kind === 'error' && typeof event.code === 'string' ? event.code : undefined;
    }
}
